import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientesService } from '../services/clientesService';
import ConfirmDialog from '../components/ConfirmDialog';
import { useConfirm } from '../hooks/useConfirm';

const CAMPOS = [
  { name: 'nombre',    label: 'Nombre',    type: 'text',  col: 1, max: 50 },
  { name: 'apellidos', label: 'Apellidos', type: 'text',  col: 1, max: 40 },
  { name: 'nifcif',    label: 'NIF/CIF',   type: 'text',  col: 1, max: 12 },
  { name: 'contacto',  label: 'Contacto',  type: 'text',  col: 1, max: 40 },
  { name: 'domicilio', label: 'Domicilio', type: 'text',  col: 2, max: 40 },
  { name: 'localidad', label: 'Localidad', type: 'text',  col: 1, max: 30 },
  { name: 'cp',        label: 'CP',        type: 'text',  col: 1, max:  5 },
  { name: 'provincia', label: 'Provincia', type: 'text',  col: 1, max: 20 },
  { name: 'telefono',  label: 'Teléfono',  type: 'tel',   col: 1, max: 15 },
  { name: 'movil',     label: 'Móvil',     type: 'tel',   col: 1, max: 15 },
  { name: 'fax',       label: 'Fax',       type: 'tel',   col: 1, max: 15 },
  { name: 'email',     label: 'Email',     type: 'email', col: 2, max: 50 },
];

const VALIDAR_CLIENTE = (form) => {
  const e = {};
  CAMPOS.forEach(({ name, max, type }) => {
    const val = form[name] || '';
    if (max && val.length > max) e[name] = `Máximo ${max} caracteres`;
    if (type === 'email' && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) e[name] = 'Email no válido';
  });
  return e;
};

function Campo({ label, valor, href }) {
  if (!valor) return null;
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      {href ? (
        <a href={href} className="text-primary-600 font-medium text-sm hover:underline">{valor}</a>
      ) : (
        <p className="text-sm font-medium text-gray-900">{valor}</p>
      )}
    </div>
  );
}

export default function ClienteDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const esNuevo = id === 'nuevo';

  const [c, setC]                 = useState(null);
  const [editando, setEditando]   = useState(esNuevo);
  const [form, setForm]           = useState({});
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [errores, setErrores]     = useState({});
  const [error, setError]         = useState(null);
  const { confirm, pedirConfirm } = useConfirm();

  useEffect(() => {
    if (esNuevo) { setForm({}); return; }
    clientesService.obtener(id)
      .then(r => { setC(r.data); setForm(r.data); })
      .catch(() => setError('No se pudo cargar el cliente'));
  }, [id, esNuevo]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setErrores(prev => ({ ...prev, [name]: undefined }));
  };

  const guardar = async () => {
    const errs = VALIDAR_CLIENTE(form);
    if (Object.keys(errs).length > 0) { setErrores(errs); return; }
    setGuardando(true);
    setError(null);
    try {
      if (esNuevo) {
        const r = await clientesService.crear(form);
        navigate(`/clientes/${r.data.id}`, { replace: true });
      } else {
        const r = await clientesService.actualizar(id, form);
        setC(r.data);
        setEditando(false);
      }
    } catch {
      setError('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    const ok = await pedirConfirm({
      titulo: 'Eliminar cliente',
      mensaje: '¿Eliminar este cliente? Esta acción no se puede deshacer.',
      peligro: true,
      textOk: 'Eliminar',
    });
    if (!ok) return;
    setEliminando(true);
    try {
      await clientesService.eliminar(id);
      navigate('/clientes');
    } catch {
      setError('Error al eliminar el cliente');
      setEliminando(false);
    }
  };

  if (error && !esNuevo) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-red-600">{error}</div>
    </div>
  );

  if (!esNuevo && !c) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );

  const titulo = esNuevo ? 'Nuevo cliente'
    : `${c?.nombre || ''} ${c?.apellidos || ''}`.trim() || 'Cliente';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">

        {/* Cabecera */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/clientes')}
            className="text-gray-500 hover:text-gray-700 text-xl font-bold">←</button>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-lg text-gray-800 truncate">{titulo}</h2>
            {c?.nifcif && <p className="text-gray-400 text-sm">{c.nifcif}</p>}
          </div>
          {!editando && !esNuevo && (
            <button onClick={() => setEditando(true)}
              className="bg-primary-600 text-white font-semibold px-3 py-1.5 rounded-lg text-sm hover:bg-primary-700 transition">
              Editar
            </button>
          )}
        </div>

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {editando ? (
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              {esNuevo ? 'Nuevo cliente' : 'Editando'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CAMPOS.map(({ name, label, type, col, max }) => (
                <div key={name} className={col === 2 ? 'sm:col-span-2' : ''}>
                  <label className="block text-xs text-gray-500 mb-1">{label}</label>
                  <input
                    type={type}
                    name={name}
                    value={form[name] || ''}
                    onChange={handleChange}
                    maxLength={max}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2
                      ${errores[name]
                        ? 'border-red-400 focus:ring-red-300 bg-red-50'
                        : 'border-gray-300 focus:ring-primary-500'}`}
                  />
                  {errores[name] && (
                    <p className="text-xs text-red-600 mt-0.5">{errores[name]}</p>
                  )}
                  {max && !errores[name] && (form[name] || '').length > max * 0.8 && (
                    <p className="text-xs text-gray-400 mt-0.5 text-right">
                      {(form[name] || '').length}/{max}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Notas</label>
              <textarea
                name="notas"
                value={form.notas || ''}
                onChange={handleChange}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={guardar} disabled={guardando}
                className="flex-1 bg-primary-600 text-white font-semibold py-2.5 rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50 transition">
                {guardando ? 'Guardando...' : '💾 Guardar'}
              </button>
              {!esNuevo && (
                <button onClick={() => setEditando(false)}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  Cancelar
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Datos personales */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Datos</h3>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Nombre"    valor={c.nombre} />
                <Campo label="Apellidos" valor={c.apellidos} />
                <Campo label="NIF/CIF"   valor={c.nifcif} />
                <Campo label="Contacto"  valor={c.contacto} />
              </div>
            </div>

            {/* Domicilio */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Domicilio</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Campo label="Dirección" valor={c.domicilio} />
                </div>
                <Campo label="Localidad" valor={c.localidad} />
                <Campo label="CP"        valor={c.cp} />
                <Campo label="Provincia" valor={c.provincia} />
              </div>
            </div>

            {/* Contacto */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Contacto</h3>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Teléfono" valor={c.telefono} href={c.telefono ? `tel:${c.telefono}` : null} />
                <Campo label="Móvil"    valor={c.movil}    href={c.movil    ? `tel:${c.movil}`    : null} />
                <Campo label="Fax"      valor={c.fax} />
                <div className="col-span-2">
                  <Campo label="Email" valor={c.email} href={c.email ? `mailto:${c.email}` : null} />
                </div>
              </div>
            </div>

            {c.notas && (
              <div className="bg-white rounded-xl shadow-sm p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Notas</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.notas}</p>
              </div>
            )}

            <div className="pb-8">
              <button onClick={eliminar} disabled={eliminando}
                className="w-full border border-red-300 text-red-600 font-semibold py-2.5 rounded-lg text-sm hover:bg-red-50 transition disabled:opacity-50">
                {eliminando ? 'Eliminando...' : '🗑️ Eliminar cliente'}
              </button>
            </div>
          </>
        )}
      </div>
      <ConfirmDialog {...confirm} abierto={confirm !== null} />
    </div>
  );
}
